import React, { useState, useEffect } from 'react';
import {
  X, Upload, CheckCircle, RefreshCw, UserPlus, FileText,
  ChevronDown, ChevronUp, Paperclip, FolderOpen, Users,
  Loader2, AlertCircle, Rocket, XCircle,
  Download, Eye, Play, Image, File, List, Grid3X3, LayoutGrid
} from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { launchRequestApi, editorApi, authApi, verticalHeadsApi, workloadApi, downloadCodeApi, getAccessToken } from '../lib/api';
import { formatDateTime, formatDate } from '../lib/utils';
import { getLaunchRequestStatusBadgeClasses } from '../constants/statusColors';
import { getVerticalBadgeClasses } from '../constants/statusColors';
import { useAuth, isAdminRole } from '../contexts/AuthContext';
import { CanvasEditor } from './CanvasEditor';
import { CanvasBrief3Step } from './CanvasBrief3Step';
import { CreativeDistributionInput } from './CreativeDistributionInput';
import { DownloadCodeModal } from './DownloadCodeModal';

interface Editor {
  id: string;
  editor_user_id?: string;  // user_id from editors table (from getOne query)
  editor_name?: string;
  display_name?: string;
  num_creatives_assigned?: number;
  creatives_completed?: number;
  editor_status?: string;
}

interface BuyerAssignment {
  id: string;
  buyer_id: string;
  buyer_name?: string;
  buyer_email?: string;
  run_qty?: number;
  test_deadline?: string;
  status?: string;
  assigned_file_ids?: string[];
}

interface UploadFile {
  id: string;
  original_filename?: string;
  s3_url?: string;
  thumbnail_url?: string;
  cloudfront_url?: string;
  file_size?: number;
  mime_type?: string;
  uploader_name?: string;
  created_at: string;
  comments?: string;
}

interface LaunchRequest {
  id: string;
  title?: string;
  request_type?: string;
  concept_notes?: string;
  num_creatives?: number;
  suggested_run_qty?: number;
  notes_to_creative?: string;
  notes_to_buyer?: string;
  platforms?: string[];
  verticals?: string[];
  primary_vertical?: string;
  delivery_deadline?: string;
  test_deadline?: string;
  committed_run_qty?: number;
  committed_test_deadline?: string;
  creative_head_id?: string;
  creative_head_name?: string;
  buyer_head_id?: string;
  buyer_head_name?: string;
  strategist_name?: string;
  created_by?: string;
  created_at: string;
  status: string;
  editors?: Editor[];
  buyers?: BuyerAssignment[];
  uploads?: UploadFile[];
  reassignments?: any[];
  upload_count?: number;
  launched_at?: string;
  closed_at?: string;
  reopened_at?: string;
  scheduled_close_at?: string;
  auto_close_enabled?: boolean;
}

interface Props {
  request: LaunchRequest;
  onClose: () => void;
  onUpdate: () => void;
}

export function LaunchRequestDetailsModal({ request: initialRequest, onClose, onUpdate }: Props) {
  const { user, featureFlags } = useAuth();
  const [request, setRequest] = useState<LaunchRequest>(initialRequest);
  const [loading, setLoading] = useState(true); // true until getOne resolves
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');

  // ── reassign state ────────────────────────────────────────────────────────
  const [showReassignCreative, setShowReassignCreative] = useState(false);
  const [showReassignBuyer, setShowReassignBuyer] = useState(false);
  const [newCreativeHeadId, setNewCreativeHeadId] = useState('');
  const [newBuyerHeadId, setNewBuyerHeadId] = useState('');
  const [reassignReason, setReassignReason] = useState('');

  // ── buyer commit state ────────────────────────────────────────────────────
  const [showBuyerCommit, setShowBuyerCommit] = useState(false);
  const [committedRunQty, setCommittedRunQty] = useState(String(request.committed_run_qty || ''));
  const [committedTestDeadline, setCommittedTestDeadline] = useState(
    request.committed_test_deadline ? request.committed_test_deadline.slice(0, 16) : ''
  );
  const [buyerAssignments, setBuyerAssignments] = useState<Array<{
    buyer_id: string; buyer_name: string; run_qty: string; test_deadline: string; selected_files: string[];
  }>>([]);

  // ── canvas state ──────────────────────────────────────────────────────────
  const [showCanvas, setShowCanvas] = useState(false);
  const [show3StepCanvas, setShow3StepCanvas] = useState(false);
  const [canvasContent, setCanvasContent] = useState<string | null>(null);

  // ── upload state (drag/drop, multiple files, folder) ─────────────────────
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadComment, setUploadComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // ── upload queue (progress sidebar) ──────────────────────────────────────
  const [uploadQueue, setUploadQueue] = useState<Array<{ name: string; status: 'pending' | 'uploading' | 'done' | 'error'; error?: string }>>([]);
  const [showUploadQueue, setShowUploadQueue] = useState(false);

  // ── download code (UCODE) ─────────────────────────────────────────────────
  const [downloadCodeUpload, setDownloadCodeUpload] = useState<{ id: string; media_file_id: string; original_filename: string } | null>(null);
  const [bulkUcodePicker, setBulkUcodePicker] = useState(false);
  const [bulkUcodeLoading, setBulkUcodeLoading] = useState(false);

  // ── upload display ────────────────────────────────────────────────────────
  const [uploadViewMode, setUploadViewMode] = useState<'list' | 'grid' | 'tile'>('list');
  const [previewUpload, setPreviewUpload] = useState<any>(null);

  const handleUploadDownload = async (upload: any) => {
    try {
      const resp = await launchRequestApi.downloadUpload(request.id, upload.id);
      const url = resp.data?.data?.url;
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.download = upload.original_filename || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleBulkUcodeDownload = async (codeType: 'normal' | 'flexible') => {
    setBulkUcodeLoading(true);
    const uploads = ((request.uploads || []) as any[]).filter(u => selectedUploadIds.includes(u.id) && u.media_file_id);
    for (const upload of uploads) {
      try {
        const resp = await downloadCodeApi.generate(upload.media_file_id, codeType);
        const data = resp.data?.data;
        if (data?.download_url && data?.download_filename) {
          const a = document.createElement('a');
          a.href = data.download_url;
          a.download = data.download_filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          await new Promise(r => setTimeout(r, 400));
        }
      } catch (err) {
        console.error('Bulk UCODE download failed for', upload.original_filename, err);
      }
    }
    setBulkUcodeLoading(false);
    setBulkUcodePicker(false);
  };

  // ── buyer file access / testing assignment ────────────────────────────────
  const [selectedUploadIds, setSelectedUploadIds] = useState<string[]>([]);
  const [showTestingAssign, setShowTestingAssign] = useState(false);
  const [testingBuyerId, setTestingBuyerId] = useState('');
  const [testingDueDate, setTestingDueDate] = useState('');
  const [testingAssigning, setTestingAssigning] = useState(false);

  // ── assign editors state (file-request style) ───────────────────────────
  const [showAssignEditors, setShowAssignEditors] = useState(false);
  const [availableEditors, setAvailableEditors] = useState<any[]>([]);
  const [editorDistribution, setEditorDistribution] = useState<Array<{ editor_id: string; num_creatives: number }>>([]);
  const [selectedEditorIds, setSelectedEditorIds] = useState<string[]>([]);
  const [editorWorkloadData, setEditorWorkloadData] = useState<Map<string, any>>(new Map());
  const [editorNotes, setEditorNotes] = useState<Record<string, string>>({});
  const [assignmentReason, setAssignmentReason] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [editorSearch, setEditorSearch] = useState('');

  // ── data ──────────────────────────────────────────────────────────────────
  const [availableBuyers, setAvailableBuyers] = useState<any[]>([]);
  const [availableCreativeUsers, setAvailableCreativeUsers] = useState<any[]>([]);
  const [showSection, setShowSection] = useState<Record<string, boolean>>({
    summary: true, creative: true, buyer: false, uploads: true, history: false
  });

  const toggleSection = (key: string) =>
    setShowSection(s => ({ ...s, [key]: !s[key] }));

  // ── fetch fresh data ──────────────────────────────────────────────────────
  const refresh = async () => {
    try {
      const res = await launchRequestApi.getOne(request.id);
      setRequest(res.data.data);
    } catch (err) {
      console.error('Failed to refresh:', err);
    }
  };

  // Load full request + supporting data on mount
  // (list query returns summary only — editors array with editor_user_id requires getOne)
  useEffect(() => {
    const loadData = async () => {
      // Load all in parallel — each with its own catch so one failure doesn't kill others
      const [fullReqRes, buyersRes, vhRes, editorsRes] = await Promise.all([
        launchRequestApi.getOne(initialRequest.id).catch((err: any) => { console.error('Failed to load request:', err); return null; }),
        authApi.getBuyers().catch((err: any) => { console.error('Failed to load buyers:', err); return null; }),
        verticalHeadsApi.getAll().catch((err: any) => { console.error('Failed to load vertical heads:', err); return null; }),
        editorApi.getAll().catch((err: any) => { console.error('Failed to load editors:', err); return null; }),
      ]);

      if (fullReqRes) setRequest(fullReqRes.data.data);
      if (buyersRes) setAvailableBuyers(buyersRes.data.data || buyersRes.data || []);
      if (editorsRes) setAvailableEditors(editorsRes.data.data || editorsRes.data || []);

      // Build VH dropdown from vertical_heads data, with fallback to creative users
      let vhList: any[] = [];
      if (vhRes) {
        const vhData = vhRes.data.data || [];
        const uniqueVHs = new Map<string, any>();
        vhData.forEach((vh: any) => {
          if (vh.head_editor_id && !uniqueVHs.has(vh.head_editor_id)) {
            uniqueVHs.set(vh.head_editor_id, {
              id: vh.head_editor_id,
              name: vh.head_editor_display_name || vh.head_editor_name || vh.vertical,
              email: vh.head_editor_email || '',
            });
          }
        });
        vhList = Array.from(uniqueVHs.values());
      }
      // Fallback: if no vertical heads found, load creative users directly
      if (vhList.length === 0) {
        try {
          const creativeRes = await authApi.getUsersByRole('creative');
          const creatives = creativeRes.data.data || creativeRes.data || [];
          vhList = creatives.map((u: any) => ({
            id: u.id,
            name: u.display_name || u.name,
            email: u.email || '',
          }));
        } catch (err) {
          console.error('Fallback: failed to load creative users:', err);
        }
      }
      setAvailableCreativeUsers(vhList);

      setLoading(false);
    };
    loadData();
  }, [initialRequest.id]);

  const isAdmin = isAdminRole(user?.role, user?.additional_roles);
  const isCreativeHead = user?.id === request.creative_head_id;
  const isBuyerHead = user?.id === request.buyer_head_id;
  const isStrategist = user?.id === request.created_by || isAdmin;
  const isBuyer = user?.role === 'buyer';

  // ── action handlers ───────────────────────────────────────────────────────

  const doAction = async (action: string, fn: () => Promise<any>) => {
    setActionLoading(action);
    setError('');
    try {
      await fn();
      await refresh();
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed: ${action}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleSubmit = () => doAction('submit', () => launchRequestApi.submit(request.id));
  const handleAccept = () => doAction('accept', () => launchRequestApi.acceptByCreativeHead(request.id));
  const handleMarkReady = () => doAction('mark-ready', () => launchRequestApi.markReadyToLaunch(request.id));
  const handleLaunch = () => doAction('launch', () => launchRequestApi.launch(request.id));
  const handleClose = () => doAction('close', () => launchRequestApi.close(request.id));
  const handleReopen = () => doAction('reopen', () => launchRequestApi.reopen(request.id));
  const handleScheduleClose = async () => {
    try {
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
      const token = getAccessToken();
      await fetch(`${API_BASE_URL}/launch-requests/${request.id}/schedule-close`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hours: 24 })
      });
      alert('Request scheduled to close in 24 hours');
      onUpdate();
    } catch (error) {
      console.error('Failed to schedule close:', error);
      alert('Failed to schedule auto-close');
    }
  };

  const handleReassignCreative = async () => {
    if (!newCreativeHeadId) return;
    await doAction('reassign-creative', () =>
      launchRequestApi.reassignCreativeHead(request.id, { new_creative_head_id: newCreativeHeadId, reason: reassignReason })
    );
    setShowReassignCreative(false);
    setNewCreativeHeadId('');
    setReassignReason('');
  };

  const handleReassignBuyer = async () => {
    if (!newBuyerHeadId) return;
    await doAction('reassign-buyer', () =>
      launchRequestApi.reassignBuyerHead(request.id, { new_buyer_head_id: newBuyerHeadId, reason: reassignReason })
    );
    setShowReassignBuyer(false);
    setNewBuyerHeadId('');
    setReassignReason('');
  };

  const handleAssignBuyers = async () => {
    const assignments = buyerAssignments.map(a => ({
      buyer_id: a.buyer_id,
      file_ids: a.selected_files,
      run_qty: a.run_qty ? Number(a.run_qty) : undefined,
      test_deadline: a.test_deadline || undefined
    }));
    await doAction('assign-buyers', () =>
      launchRequestApi.assignBuyers(request.id, {
        buyer_assignments: assignments,
        committed_run_qty: committedRunQty ? Number(committedRunQty) : undefined,
        committed_test_deadline: committedTestDeadline || undefined
      })
    );
    setShowBuyerCommit(false);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);
    try {
      const { uploadQueueManager } = await import('../services/uploadQueueManager');
      uploadQueueManager.addToQueue(
        selectedFiles,
        request.id,
        uploadComment || undefined,
        undefined,
        undefined,
        'launch-requests'
      );
      setShowUploadQueue(true);
      setSelectedFiles([]);
      setUploadComment('');
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (err: any) {
      setUploadError(err.message || 'Failed to start upload');
    } finally {
      setUploading(false);
    }
  };

  // Track queue progress for this request
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let lastAllDone = false;
    const setup = async () => {
      const { uploadQueueManager } = await import('../services/uploadQueueManager');
      unsubscribe = uploadQueueManager.subscribe((queue) => {
        const relevant = queue.filter(t =>
          t.requestId === request.id &&
          (t.status !== 'completed' || (t.endTime && Date.now() - t.endTime < 10000))
        );
        if (relevant.length === 0) return;
        setUploadQueue(relevant.map(t => ({
          name: (t as any).fileName || t.file?.name || 'file',
          status: t.status === 'completed' ? 'done' : t.status === 'failed' ? 'error' : t.status === 'uploading' ? 'uploading' : 'pending',
          error: t.error,
        })));
        setShowUploadQueue(true);
        const allDone = relevant.every(t => t.status === 'completed' || t.status === 'failed');
        if (allDone && !lastAllDone) {
          lastAllDone = true;
          refresh();
          try { onUpdate(); } catch (_) {}
          setTimeout(() => setShowUploadQueue(false), 5000);
        } else if (!allDone) {
          lastAllDone = false;
        }
      });
    };
    setup();
    return () => { if (unsubscribe) unsubscribe(); };
  }, [request.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };

  // Recursive folder traversal for drag-and-drop (Dropbox-style)
  const traverseFileTree = async (item: any, files: File[]): Promise<void> => {
    if (item.isFile) {
      return new Promise((resolve) => {
        item.file((file: File) => { files.push(file); resolve(); });
      });
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      const readAllEntries = (): Promise<any[]> => {
        return new Promise((resolve) => {
          const allEntries: any[] = [];
          const readBatch = () => {
            dirReader.readEntries((entries: any[]) => {
              if (entries.length === 0) resolve(allEntries);
              else { allEntries.push(...entries); readBatch(); }
            });
          };
          readBatch();
        });
      };
      const entries = await readAllEntries();
      for (const entry of entries) {
        await traverseFileTree(entry, files);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const items = Array.from(e.dataTransfer.items);
    const files: File[] = [];

    const processItems = async () => {
      if (items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
        for (const item of items) {
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            if (entry) await traverseFileTree(entry, files);
          }
        }
      } else {
        // Fallback for browsers without webkitGetAsEntry
        files.push(...Array.from(e.dataTransfer.files));
      }
      if (files.length > 0) setSelectedFiles(prev => [...prev, ...files]);
    };
    processItems();
  };

  // Assign selected files to a buyer for testing (uses existing assignBuyers endpoint)
  const handleTestingAssign = async () => {
    if (!testingBuyerId || selectedUploadIds.length === 0) return;
    setTestingAssigning(true);
    try {
      // Merge with existing buyer assignments — add/update this buyer's files
      const existingBuyers = (request.buyers || []).map(b => ({
        buyer_id: b.buyer_id,
        file_ids: (b.assigned_file_ids || []).map(String),
        run_qty: b.run_qty,
        test_deadline: b.test_deadline || undefined
      }));
      // Find if buyer already exists in assignments
      const existingIdx = existingBuyers.findIndex(b => b.buyer_id === testingBuyerId);
      if (existingIdx >= 0) {
        // Merge file IDs (deduplicate)
        const merged = Array.from(new Set([...existingBuyers[existingIdx].file_ids, ...selectedUploadIds]));
        existingBuyers[existingIdx] = {
          ...existingBuyers[existingIdx],
          file_ids: merged,
          test_deadline: testingDueDate || existingBuyers[existingIdx].test_deadline
        };
      } else {
        existingBuyers.push({
          buyer_id: testingBuyerId,
          file_ids: selectedUploadIds,
          run_qty: undefined,
          test_deadline: testingDueDate || undefined
        });
      }
      await launchRequestApi.assignBuyers(request.id, { buyer_assignments: existingBuyers });
      await refresh();
      onUpdate();
      setSelectedUploadIds([]);
      setShowTestingAssign(false);
      setTestingBuyerId('');
      setTestingDueDate('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign files');
    } finally {
      setTestingAssigning(false);
    }
  };

  const handleRevokeAccess = async (buyerId: string, buyerName: string) => {
    if (!window.confirm(`Revoke ${buyerName}'s access? This will hide all assigned files from their Media Library.`)) return;
    try {
      await launchRequestApi.revokeAccess(request.id, buyerId);
      await refresh();
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke access');
    }
  };

  const handleAssignEditors = async () => {
    if (selectedEditorIds.length === 0) {
      setError('Please select at least one editor');
      return;
    }
    setAssignSaving(true);
    try {
      const payload: any = {};
      if (editorDistribution.length > 0) {
        payload.editor_distribution = editorDistribution;
      } else {
        payload.editor_ids = selectedEditorIds;
      }
      // Include notes if provided
      if (Object.keys(editorNotes).some(k => editorNotes[k])) {
        payload.editor_notes = editorNotes;
      }
      if (assignmentReason) {
        payload.reason = assignmentReason;
      }
      await launchRequestApi.assignEditors(request.id, payload);
      await refresh();
      onUpdate();
      setShowAssignEditors(false);
      setEditorDistribution([]);
      setSelectedEditorIds([]);
      setEditorNotes({});
      setAssignmentReason('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign editors');
    } finally {
      setAssignSaving(false);
    }
  };

  // Load workload data when assignment panel opens
  useEffect(() => {
    if (!showAssignEditors) return;
    const loadWorkload = async () => {
      try {
        const response = await workloadApi.getOverview();
        const workloads = response.data.data?.editors || [];
        const workloadMap = new Map<string, any>();
        workloads.forEach((w: any) => {
          workloadMap.set(w.id, {
            editorId: w.id,
            editorName: w.name,
            activeRequests: w.activeRequests,
            loadPercentage: w.loadPercentage,
            maxConcurrentRequests: w.maxConcurrentRequests,
            isAvailable: w.isAvailable,
          });
        });
        setEditorWorkloadData(workloadMap);
      } catch (err) {
        console.error('Failed to fetch workload data:', err);
      }

      // Pre-select existing editors
      if (request.editors && request.editors.length > 0) {
        const activeEditors = request.editors.filter(
          (e: any) => e.editor_status !== 'removed' && e.editor_status !== 'reassigned'
        );
        setSelectedEditorIds(activeEditors.map((e: any) => e.id));
        const dist = activeEditors
          .filter((e: any) => e.num_creatives_assigned && e.num_creatives_assigned > 0)
          .map((e: any) => ({ editor_id: e.id, num_creatives: e.num_creatives_assigned }));
        if (dist.length > 0) setEditorDistribution(dist);
      }
    };
    loadWorkload();
  }, [showAssignEditors]);

  const toggleEditorSelection = (editorId: string) => {
    setSelectedEditorIds(prev =>
      prev.includes(editorId) ? prev.filter(id => id !== editorId) : [...prev, editorId]
    );
  };

  // init buyer assignments when opening commit panel
  useEffect(() => {
    if (showBuyerCommit && request.buyers) {
      setBuyerAssignments(
        (request.buyers || []).map(b => ({
          buyer_id: b.buyer_id,
          buyer_name: b.buyer_name || '',
          run_qty: String(b.run_qty || ''),
          test_deadline: b.test_deadline ? b.test_deadline.slice(0, 16) : '',
          selected_files: b.assigned_file_ids?.map(String) || []
        }))
      );
    }
  }, [showBuyerCommit]);

  const statusLabel = (request.status || 'draft').replace(/_/g, ' ');

  // ── progress bar helper ───────────────────────────────────────────────────
  const ProgressBar = ({ uploaded, total }: { uploaded: number; total: number }) => {
    const pct = total > 0 ? Math.min(100, Math.round((uploaded / total) * 100)) : 0;
    const barColor = pct >= 100 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-400' : 'bg-blue-500';
    return (
      <div className="min-w-[100px]">
        <div className="flex justify-between text-xs mb-1">
          <span>{uploaded}/{total}</span>
          <span className="font-medium">{pct}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-1.5">
          <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const SectionHeader = ({ title, sectionKey }: { title: string; sectionKey: string }) => (
    <button
      className="flex items-center justify-between w-full py-2 text-sm font-semibold text-left"
      onClick={() => toggleSection(sectionKey)}
    >
      <span>{title}</span>
      {showSection[sectionKey] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
    </button>
  );

  // Loading spinner shown while getOne resolves
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-background rounded-xl p-8 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  // Canvas brief overlay — renders on top of everything
  if (showCanvas) {
    return (
      <CanvasEditor
        requestId={request.id}
        canvasApiOverride={launchRequestApi.canvas}
        onClose={() => setShowCanvas(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-background rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-3xl max-h-[90vh] flex flex-col">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between p-6 border-b shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={getLaunchRequestStatusBadgeClasses(request.status)}>
                {statusLabel}
              </span>
              {request.platforms?.map((p, i) => (
                <span key={i} className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {p}
                </span>
              ))}
              {request.verticals?.map((v, i) => (
                <span key={i} className={getVerticalBadgeClasses(v)}>{v}</span>
              ))}
            </div>
            <h2 className="text-lg font-semibold mt-2 truncate">
              {request.title || request.request_type || 'Launch Request'}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              By {request.strategist_name || 'Strategist'} · {formatDateTime(request.created_at)}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mx-6 mt-4 p-3 bg-destructive/10 text-destructive text-sm rounded-lg">{error}</div>
          )}

          {/* ─── Action Buttons ─────────────────────────────────────────── */}
          <div className="px-6 py-4 border-b flex flex-wrap gap-2">
            {/* Strategist: submit draft */}
            {isStrategist && request.status === 'draft' && (
              <Button size="sm" onClick={handleSubmit} disabled={!!actionLoading}>
                {actionLoading === 'submit' ? 'Submitting...' : 'Submit for Review'}
              </Button>
            )}

            {/* Vertical head: accept — only admin/creative role, never buyer */}
            {(isCreativeHead || isAdmin) && user?.role !== 'buyer' && request.status === 'pending_review' && (
              <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white" onClick={handleAccept} disabled={!!actionLoading}>
                {actionLoading === 'accept' ? 'Accepting...' : 'Accept (Start Production)'}
              </Button>
            )}

            {/* Upload Creative panel is shown inline below the action bar (always visible for non-buyers during in_production) */}

            {/* Vertical head (or admin): mark ready to launch — only admin/creative role, never buyer */}
            {(isCreativeHead || isAdmin) && user?.role !== 'buyer' && request.status === 'in_production' && (
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleMarkReady} disabled={!!actionLoading}>
                {actionLoading === 'mark-ready' ? 'Saving...' : 'Mark Ready to Launch'}
              </Button>
            )}

            {/* Buyer head: assign files to buyers */}
            {(isBuyerHead || isAdmin) && request.status === 'ready_to_launch' && (
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setShowBuyerCommit(v => !v)}>
                <UserPlus className="w-4 h-4 mr-1.5" />
                {showBuyerCommit ? 'Hide Assignment' : 'Assign to Buyers'}
              </Button>
            )}

            {/* Buyer: mark launched */}
            {(isBuyer || isAdmin) && request.status === 'buyer_assigned' && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleLaunch} disabled={!!actionLoading}>
                {actionLoading === 'launch' ? 'Launching...' : 'Mark Launched'}
              </Button>
            )}

            {/* Close */}
            {(isAdmin || isStrategist) && request.status === 'launched' && (
              <>
                <Button size="sm" variant="outline" onClick={handleClose} disabled={!!actionLoading}>
                  {actionLoading === 'close' ? 'Closing...' : 'Close Request'}
                </Button>
                <Button size="sm" variant="outline" onClick={handleScheduleClose} disabled={!!actionLoading || !!request.scheduled_close_at}>
                  {request.scheduled_close_at ? 'Scheduled' : 'Close in 24hrs'}
                </Button>
              </>
            )}

            {/* Reopen */}
            {(isAdmin || isStrategist) && request.status === 'closed' && (
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleReopen} disabled={!!actionLoading}>
                <RefreshCw className="w-4 h-4 mr-1.5" />
                {actionLoading === 'reopen' ? 'Reopening...' : 'Reopen'}
              </Button>
            )}

            {/* Vertical head: assign/reassign editors */}
            {(isCreativeHead || isAdmin) && user?.role !== 'buyer' && !['closed', 'launched'].includes(request.status) && (
              <Button size="sm" variant="outline" onClick={() => setShowAssignEditors(v => !v)}>
                <Users className="w-4 h-4 mr-1.5" />
                {showAssignEditors ? 'Hide Editor Assignment' : 'Assign Editors'}
              </Button>
            )}

            {/* Reassign vertical head — only admin/creative role */}
            {(isCreativeHead || isAdmin) && user?.role !== 'buyer' && !['closed'].includes(request.status) && (
              <Button size="sm" variant="outline" onClick={() => setShowReassignCreative(v => !v)}>
                Reassign Vertical Head
              </Button>
            )}

            {/* Reassign buyer head */}
            {(isBuyerHead || isAdmin) && !['closed'].includes(request.status) && (
              <Button size="sm" variant="outline" onClick={() => setShowReassignBuyer(v => !v)}>
                Reassign Buyer Head
              </Button>
            )}
          </div>

          {/* ─── Upload Panel (visible for non-buyers when in production or pending review) ── */}
          {user?.role !== 'buyer' && ['in_production', 'pending_review'].includes(request.status) && (
            <div className="mx-6 mt-4">
              <form onSubmit={handleUpload} className="p-4 border rounded-lg space-y-3 bg-muted/30">
                <h4 className="text-sm font-semibold">Upload Creative Files</h4>

                {/* Drag & Drop Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative p-4 border-2 border-dashed rounded-lg transition-colors ${
                    isDragging ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  {isDragging && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary/5 rounded-lg pointer-events-none z-10">
                      <div className="text-center">
                        <Upload className="w-10 h-10 mx-auto mb-1 text-primary" />
                        <p className="text-sm font-medium text-primary">Drop files or folders here</p>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    {/* File input */}
                    <input
                      type="file"
                      multiple
                      onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; setSelectedFiles(prev => [...prev, ...files]); }}
                      className="w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary/10 file:text-primary file:text-sm hover:file:bg-primary/20"
                      disabled={uploading}
                    />
                    {/* Folder input */}
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground">Or select entire folder:</span>
                      <input
                        type="file"
                        // @ts-ignore
                        webkitdirectory="true"
                        directory="true"
                        multiple
                        onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; setSelectedFiles(prev => [...prev, ...files]); }}
                        className="text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-green-100 file:text-green-700 file:text-sm hover:file:bg-green-200"
                        disabled={uploading}
                      />
                    </div>
                    {!isDragging && selectedFiles.length === 0 && (
                      <p className="text-xs text-center text-muted-foreground py-2">
                        Or drag and drop files / folders here
                      </p>
                    )}
                  </div>
                </div>

                {/* Selected file list */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">{selectedFiles.length} file(s) selected:</p>
                    <div className="max-h-28 overflow-y-auto space-y-1">
                      {selectedFiles.map((f, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-muted px-2 py-1 rounded">
                          <span className="truncate">{f.name}</span>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className="text-muted-foreground">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                            <button type="button" className="text-destructive hover:underline" onClick={() => setSelectedFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments */}
                <textarea
                  value={uploadComment}
                  onChange={e => setUploadComment(e.target.value)}
                  placeholder="Comments / remarks (optional)"
                  rows={2}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  disabled={uploading}
                />

                {uploadError && (
                  <div className="p-2 bg-destructive/10 text-destructive text-xs rounded">{uploadError}</div>
                )}
                {uploadSuccess && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 text-green-700 text-xs rounded">
                    <CheckCircle className="w-4 h-4" /> Files uploaded successfully!
                  </div>
                )}

                <div className="flex gap-2">
                  <Button type="submit" size="sm" disabled={selectedFiles.length === 0 || uploading}>
                    {uploading ? 'Uploading...' : `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
                  </Button>
                  {selectedFiles.length > 0 && (
                    <Button type="button" size="sm" variant="outline" onClick={() => setSelectedFiles([])}>Clear</Button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* ─── Assign Editors Panel (File-Request Style) ─────────────── */}
          {showAssignEditors && (
            <div className="mx-6 mt-4 p-4 border rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-blue-500" />
                    Assign Editors & Distribute Creatives
                  </h4>
                  <p className="text-xs text-muted-foreground mt-0.5">Total creatives: {request.num_creatives || 0}</p>
                </div>
                <button onClick={() => setShowAssignEditors(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Current Assignments */}
              {request.editors && request.editors.filter((e: any) => e.editor_status !== 'removed').length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1.5">Currently assigned:</p>
                  <div className="space-y-1">
                    {request.editors.filter((e: any) => e.editor_status !== 'removed').map((e: any) => (
                      <div key={e.id} className="flex items-center gap-2 text-xs">
                        <span className="text-blue-800 dark:text-blue-200">{e.display_name || e.editor_name || e.id}</span>
                        <span className={`px-1.5 py-0.5 rounded ${
                          e.editor_status === 'completed' ? 'bg-green-200 text-green-800' :
                          e.editor_status === 'in_progress' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-blue-200 text-blue-800'
                        }`}>{e.editor_status || 'pending'}</span>
                        {e.num_creatives_assigned > 0 && (
                          <span className="text-blue-600 dark:text-blue-300">
                            {e.creatives_completed || 0}/{e.num_creatives_assigned} done
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Editor Selection with Workload */}
              <div>
                <label className="block text-xs font-medium mb-2">Select Editors</label>
                <input
                  type="text"
                  placeholder="Search editors..."
                  value={editorSearch}
                  onChange={e => setEditorSearch(e.target.value)}
                  className="w-full mb-2 px-3 py-1.5 text-sm border rounded-md bg-background focus:ring-2 focus:ring-ring focus:outline-none"
                />
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {availableEditors
                    .filter((e: any) => e.is_active !== false)
                    .filter((e: any) => {
                      if (!editorSearch.trim()) return true;
                      const term = editorSearch.toLowerCase();
                      return (e.display_name || e.name || '').toLowerCase().includes(term);
                    })
                    .map((editor: any) => {
                    const workload = editorWorkloadData.get(editor.id);
                    const isSelected = selectedEditorIds.includes(editor.id);

                    return (
                      <div key={editor.id} className={`flex items-center gap-3 p-2.5 hover:bg-muted/50 cursor-pointer ${isSelected ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                        onClick={() => toggleEditorSelection(editor.id)}>
                        <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-4 h-4 text-blue-600 rounded pointer-events-none" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{editor.display_name || editor.name}</p>
                          {workload && (
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-muted-foreground">{workload.activeRequests} active</span>
                            </div>
                          )}
                        </div>
                        {/* No per-editor cap: only a real "Unavailable" signal is worth a badge. */}
                        {workload && !workload.isAvailable && (
                          <span className="text-xs px-2 py-0.5 rounded-full text-white bg-gray-500">
                            Unavailable
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {availableEditors.length === 0 && (
                    <div className="p-4 text-center text-sm text-muted-foreground">No editors available</div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{selectedEditorIds.length} editor(s) selected</p>
              </div>

              {/* Creative Distribution */}
              {(request.num_creatives || 0) > 0 && selectedEditorIds.length > 0 && (
                <div className="border rounded-lg p-3 bg-muted/30">
                  <h5 className="text-xs font-medium mb-2">Distribute Creatives Among Editors</h5>
                  <CreativeDistributionInput
                    editors={availableEditors.map((e: any) => ({
                      ...e,
                      workload: editorWorkloadData.get(e.id) ? {
                        loadPercentage: editorWorkloadData.get(e.id)!.loadPercentage,
                        activeRequests: editorWorkloadData.get(e.id)!.activeRequests,
                        maxConcurrentRequests: editorWorkloadData.get(e.id)!.maxConcurrentRequests,
                        isAvailable: editorWorkloadData.get(e.id)!.isAvailable,
                      } : undefined,
                    }))}
                    totalCreatives={request.num_creatives || 0}
                    selectedEditorIds={selectedEditorIds}
                    onDistributionChange={setEditorDistribution}
                  />
                </div>
              )}

              {/* Per-Editor Notes */}
              {selectedEditorIds.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-xs font-medium">Per-Editor Notes (Optional)</label>
                  {selectedEditorIds.map(editorId => {
                    const editor = availableEditors.find((e: any) => e.id === editorId);
                    if (!editor) return null;
                    return (
                      <div key={editorId}>
                        <label className="text-xs text-muted-foreground">{editor.display_name || editor.name}</label>
                        <textarea
                          value={editorNotes[editorId] || ''}
                          onChange={e => setEditorNotes(prev => ({ ...prev, [editorId]: e.target.value }))}
                          placeholder={`Notes for ${editor.display_name || editor.name}...`}
                          rows={1}
                          className="w-full px-2 py-1.5 text-sm border rounded-md bg-background resize-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Assignment Reason */}
              <div>
                <label className="block text-xs font-medium mb-1">Assignment Notes (Optional)</label>
                <textarea
                  value={assignmentReason}
                  onChange={e => setAssignmentReason(e.target.value)}
                  placeholder="Add notes visible to all assigned editors..."
                  rows={2}
                  className="w-full px-2 py-1.5 text-sm border rounded-md bg-background resize-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1 border-t">
                <Button size="sm" onClick={handleAssignEditors} disabled={selectedEditorIds.length === 0 || assignSaving}>
                  {assignSaving ? 'Saving...' : 'Save Editor Assignment'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAssignEditors(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* ─── Buyer Commit Panel ──────────────────────────────────────── */}
          {showBuyerCommit && (
            <div className="mx-6 mt-4 p-4 border rounded-lg space-y-4">
              <h4 className="text-sm font-semibold">Assign Uploaded Creatives to Buyers</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Overall Committed Run Qty</label>
                  <Input
                    type="number"
                    min={1}
                    placeholder="e.g. 5"
                    value={committedRunQty}
                    onChange={e => setCommittedRunQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Overall Test Deadline</label>
                  <Input
                    type="datetime-local"
                    value={committedTestDeadline}
                    onChange={e => setCommittedTestDeadline(e.target.value)}
                  />
                </div>
              </div>

              {/* Per-buyer assignment */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Per-buyer assignments</span>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setBuyerAssignments(prev => [...prev, { buyer_id: '', buyer_name: '', run_qty: '', test_deadline: '', selected_files: [] }])}
                  >
                    + Add Buyer
                  </Button>
                </div>
                {buyerAssignments.map((assignment, idx) => (
                  <div key={idx} className="p-3 border rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium mb-1">Media Buyer</label>
                        <select
                          className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                          value={assignment.buyer_id}
                          onChange={e => setBuyerAssignments(prev => prev.map((a, i) => i === idx ? { ...a, buyer_id: e.target.value } : a))}
                        >
                          <option value="">— Select buyer —</option>
                          {availableBuyers.map(b => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Run Qty</label>
                        <Input
                          type="number" min={1} placeholder="Qty"
                          value={assignment.run_qty}
                          onChange={e => setBuyerAssignments(prev => prev.map((a, i) => i === idx ? { ...a, run_qty: e.target.value } : a))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Test Deadline</label>
                      <Input
                        type="datetime-local"
                        value={assignment.test_deadline}
                        onChange={e => setBuyerAssignments(prev => prev.map((a, i) => i === idx ? { ...a, test_deadline: e.target.value } : a))}
                      />
                    </div>
                    {/* File selection from uploads */}
                    {(request.uploads || []).length > 0 && (
                      <div>
                        <label className="block text-xs font-medium mb-1">Select files for this buyer</label>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {(request.uploads || []).map(upload => (
                            <label key={upload.id} className="flex items-center gap-2 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={assignment.selected_files.includes(upload.id)}
                                onChange={e => {
                                  setBuyerAssignments(prev => prev.map((a, i) => {
                                    if (i !== idx) return a;
                                    const files = e.target.checked
                                      ? [...a.selected_files, upload.id]
                                      : a.selected_files.filter(f => f !== upload.id);
                                    return { ...a, selected_files: files };
                                  }));
                                }}
                              />
                              <Paperclip className="w-3 h-3 text-muted-foreground" />
                              <span className="truncate">{upload.original_filename}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      className="text-xs text-destructive hover:underline"
                      onClick={() => setBuyerAssignments(prev => prev.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={handleAssignBuyers} disabled={!!actionLoading}>
                  {actionLoading === 'assign-buyers' ? 'Assigning...' : 'Confirm Assignments'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowBuyerCommit(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* ─── Reassign Panels ─────────────────────────────────────────── */}
          {showReassignCreative && (
            <div className="mx-6 mt-4 p-4 border rounded-lg space-y-3">
              <h4 className="text-sm font-semibold">Reassign Vertical Head</h4>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={newCreativeHeadId}
                onChange={e => setNewCreativeHeadId(e.target.value)}
              >
                <option value="">— Select new vertical head —</option>
                {availableCreativeUsers.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <Input placeholder="Reason (optional)" value={reassignReason} onChange={e => setReassignReason(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleReassignCreative} disabled={!newCreativeHeadId || !!actionLoading}>Confirm</Button>
                <Button size="sm" variant="outline" onClick={() => setShowReassignCreative(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {showReassignBuyer && (
            <div className="mx-6 mt-4 p-4 border rounded-lg space-y-3">
              <h4 className="text-sm font-semibold">Reassign Buyer Head</h4>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={newBuyerHeadId}
                onChange={e => setNewBuyerHeadId(e.target.value)}
              >
                <option value="">— Select new buyer head —</option>
                {availableBuyers.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <Input placeholder="Reason (optional)" value={reassignReason} onChange={e => setReassignReason(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleReassignBuyer} disabled={!newBuyerHeadId || !!actionLoading}>Confirm</Button>
                <Button size="sm" variant="outline" onClick={() => setShowReassignBuyer(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* ─── Body Sections ───────────────────────────────────────────── */}
          <div className="px-6 py-4 space-y-0 divide-y">

            {/* Summary */}
            <div className="pb-4">
              <SectionHeader title="Request Summary" sectionKey="summary" />
              {showSection.summary && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm mt-2">
                  <div><span className="text-muted-foreground">Type: </span>{request.request_type || '—'}</div>
                  <div><span className="text-muted-foreground">Creatives: </span>
                    <ProgressBar uploaded={Number(request.upload_count ?? 0)} total={request.num_creatives || 0} />
                  </div>
                  <div><span className="text-muted-foreground">Suggested Run Qty: </span>{request.suggested_run_qty ?? '—'}</div>
                  <div><span className="text-muted-foreground">Committed Run Qty: </span>{request.committed_run_qty ?? '—'}</div>
                  <div><span className="text-muted-foreground">Delivery Deadline: </span>
                    {request.delivery_deadline ? formatDate(request.delivery_deadline) : '—'}
                  </div>
                  <div><span className="text-muted-foreground">Test Deadline: </span>
                    {request.committed_test_deadline
                      ? formatDate(request.committed_test_deadline)
                      : request.test_deadline
                      ? formatDate(request.test_deadline)
                      : '—'}
                  </div>
                  {request.launched_at && (
                    <div className="flex items-center gap-1.5">
                      <Rocket className="w-4 h-4 text-green-500" />
                      <span className="text-muted-foreground">Launched: </span>
                      <span>{formatDateTime(request.launched_at)}</span>
                    </div>
                  )}
                  {request.closed_at && (
                    <div className="flex items-center gap-1.5">
                      <XCircle className="w-4 h-4 text-gray-500" />
                      <span className="text-muted-foreground">Closed: </span>
                      <span>{formatDateTime(request.closed_at)}</span>
                    </div>
                  )}
                  {request.concept_notes && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Concept Notes: </span>
                      <span className="whitespace-pre-wrap">{request.concept_notes}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Creative Side */}
            <div className="py-4">
              <SectionHeader title="Creative Team" sectionKey="creative" />
              {showSection.creative && (
                <div className="mt-2 space-y-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Vertical Head: </span>
                    <span className="font-medium">{request.creative_head_name || '—'}</span>
                  </div>
                  {(request.editors || []).length > 0 && (
                    <div>
                      <p className="text-muted-foreground mb-1.5">Assigned Editors:</p>
                      <div className="space-y-1.5">
                        {(request.editors || []).map((e, i) => (
                          <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                            <span className="font-medium">{e.display_name || e.editor_name}</span>
                            <div className="flex items-center gap-4">
                              <ProgressBar
                                uploaded={e.creatives_completed || 0}
                                total={e.num_creatives_assigned || 0}
                              />
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                e.editor_status === 'completed' ? 'bg-green-100 text-green-700'
                                : e.editor_status === 'in_progress' ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-600'
                              }`}>
                                {e.editor_status || 'pending'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {request.notes_to_creative && (
                    <div>
                      <p className="text-muted-foreground mb-1">Notes to Creative:</p>
                      <p className="text-sm bg-muted/50 rounded-lg px-3 py-2 whitespace-pre-wrap">{request.notes_to_creative}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Canvas Brief — always visible in body (same as FileRequestDetailsModal) */}
            <div className="py-4">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-semibold">Canvas Brief</span>
                <Button size="sm" variant="outline" onClick={() => setShowCanvas(true)}>
                  <FileText className="w-4 h-4 mr-1.5" />
                  {showCanvas ? 'Close Canvas' : 'View / Edit Canvas'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Creative brief, references, and production notes for this request.</p>
            </div>

            {/* Buyer Side */}
            <div className="py-4">
              <SectionHeader title="Buyer Team" sectionKey="buyer" />
              {showSection.buyer && (
                <div className="mt-2 space-y-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Buyer Head: </span>
                    <span className="font-medium">{request.buyer_head_name || '—'}</span>
                  </div>
                  {(request.buyers || []).length > 0 && (
                    <div>
                      <p className="text-muted-foreground mb-1.5">Assigned Buyers:</p>
                      <div className="space-y-2">
                        {(request.buyers || []).map((b, i) => {
                          // Find the actual file objects for this buyer's assigned file IDs
                          const assignedFiles = (request.uploads || []).filter(u =>
                            (b.assigned_file_ids || []).map(String).includes(String(u.id))
                          );
                          return (
                            <div key={i} className="bg-muted/50 rounded-lg px-3 py-2 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{b.buyer_name}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    b.status === 'launched' ? 'bg-green-100 text-green-700'
                                    : 'bg-indigo-100 text-indigo-700'
                                  }`}>
                                    {b.status || 'assigned'}
                                  </span>
                                  {(isBuyerHead || isAdmin) && b.buyer_id && (
                                    <button
                                      onClick={() => handleRevokeAccess(b.buyer_id!, b.buyer_name || 'this buyer')}
                                      className="text-xs text-red-500 hover:text-red-700 hover:underline font-medium"
                                      title="Revoke access — hides files from buyer's Media Library"
                                    >
                                      Revoke
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-4 text-xs text-muted-foreground">
                                {b.run_qty && <span>Run qty: {b.run_qty}</span>}
                                {b.test_deadline && <span>Test by: {formatDate(b.test_deadline)}</span>}
                              </div>
                              {/* Files assigned to this buyer */}
                              {assignedFiles.length > 0 && (
                                <div className="space-y-1 border-t pt-2 mt-1">
                                  <p className="text-xs font-medium text-muted-foreground">
                                    Assigned files ({assignedFiles.length}):
                                  </p>
                                  {assignedFiles.map(f => (
                                    <div key={f.id} className="flex items-center gap-2 text-xs bg-background rounded px-2 py-1.5 border">
                                      <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
                                      <span className="flex-1 truncate">{f.original_filename}</span>
                                      {f.file_size && (
                                        <span className="text-muted-foreground shrink-0">{(f.file_size / 1024 / 1024).toFixed(2)} MB</span>
                                      )}
                                      {f.s3_url && (
                                        <a href={f.s3_url} target="_blank" rel="noopener noreferrer"
                                          className="text-blue-600 hover:underline font-medium shrink-0">
                                          Download
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {(b.assigned_file_ids || []).length > 0 && assignedFiles.length === 0 && (
                                <p className="text-xs text-muted-foreground italic">{b.assigned_file_ids!.length} file(s) assigned</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {request.notes_to_buyer && (
                    <div>
                      <p className="text-muted-foreground mb-1">Notes to Buyer:</p>
                      <p className="text-sm bg-muted/50 rounded-lg px-3 py-2 whitespace-pre-wrap">{request.notes_to_buyer}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Uploads */}
            <div className="py-4">
              <SectionHeader title={`Uploaded Files (${(request.uploads || []).length})`} sectionKey="uploads" />
              {showSection.uploads && (
                <div className="mt-2 space-y-2">
                  {(request.uploads || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No files uploaded yet.</p>
                  ) : (
                    <>
                      {/* Select All / Give Access for Testing toolbar — visible to buyer head/admin */}
                      {(isBuyerHead || isAdmin) && (
                        <div className="flex items-center gap-3 pb-2">
                          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                            <input
                              type="checkbox"
                              className="rounded border-border w-4 h-4"
                              checked={selectedUploadIds.length === (request.uploads || []).length && (request.uploads || []).length > 0}
                              onChange={e => {
                                if (e.target.checked) setSelectedUploadIds((request.uploads || []).map(u => u.id));
                                else setSelectedUploadIds([]);
                              }}
                            />
                            <span>Select All</span>
                          </label>
                          {selectedUploadIds.length > 0 && (
                            <Button
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7 px-3"
                              onClick={() => setShowTestingAssign(v => !v)}
                            >
                              <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                              Give Access for Testing ({selectedUploadIds.length})
                            </Button>
                          )}
                          {selectedUploadIds.length > 0 && (
                            <button
                              className="text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => setSelectedUploadIds([])}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      )}

                      {/* Testing assignment panel */}
                      {showTestingAssign && (
                        <div className="p-3 border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-800 rounded-lg space-y-3 mb-2">
                          <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                            Assign {selectedUploadIds.length} file(s) to a buyer for testing
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium mb-1">Buyer</label>
                              <select
                                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                                value={testingBuyerId}
                                onChange={e => setTestingBuyerId(e.target.value)}
                              >
                                <option value="">— Select buyer —</option>
                                {availableBuyers.map(b => (
                                  <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">Test by (due date)</label>
                              <input
                                type="datetime-local"
                                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                                value={testingDueDate}
                                onChange={e => setTestingDueDate(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleTestingAssign}
                              disabled={!testingBuyerId || testingAssigning}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                              {testingAssigning ? 'Assigning...' : 'Confirm'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setShowTestingAssign(false); setTestingBuyerId(''); setTestingDueDate(''); }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* View mode toggle + search */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                          {([['list', List, 'List'], ['grid', Grid3X3, 'Grid'], ['tile', LayoutGrid, 'Tiles']] as const).map(([mode, Icon, label]) => (
                            <button
                              key={mode}
                              onClick={() => setUploadViewMode(mode)}
                              title={label}
                              className={`p-1.5 transition-colors ${uploadViewMode === mode ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </button>
                          ))}
                        </div>
                        {selectedUploadIds.length > 0 && (
                          bulkUcodePicker ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Campaign type:</span>
                              <button onClick={() => handleBulkUcodeDownload('normal')} disabled={bulkUcodeLoading}
                                className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 font-semibold disabled:opacity-50">Normal</button>
                              <button onClick={() => handleBulkUcodeDownload('flexible')} disabled={bulkUcodeLoading}
                                className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 font-semibold disabled:opacity-50">Flex</button>
                              {bulkUcodeLoading && <Loader2 className="w-3 h-3 animate-spin text-purple-600" />}
                              {!bulkUcodeLoading && <button onClick={() => setBulkUcodePicker(false)} className="text-xs text-muted-foreground hover:underline">Cancel</button>}
                            </div>
                          ) : (
                            <button onClick={() => setBulkUcodePicker(true)}
                              className="px-3 py-1.5 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-semibold">
                              Download Selected ({selectedUploadIds.length})
                            </button>
                          )
                        )}
                      </div>

                      {/* File display based on view mode */}
                      {uploadViewMode === 'grid' ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {(request.uploads || []).map((upload: any) => {
                            const isImage = (upload.mime_type || '').startsWith('image');
                            const isVideo = (upload.mime_type || '').startsWith('video');
                            return (
                              <div key={upload.id} className={`relative border rounded-lg overflow-hidden cursor-pointer group transition-all ${
                                selectedUploadIds.includes(upload.id) ? 'ring-2 ring-blue-500 border-blue-400' : 'hover:border-blue-300'
                              }`}>
                                {(isBuyerHead || isAdmin) && (
                                  <input type="checkbox" className="absolute top-2 left-2 z-10 w-4 h-4 rounded"
                                    checked={selectedUploadIds.includes(upload.id)}
                                    onChange={e => {
                                      if (e.target.checked) setSelectedUploadIds(prev => [...prev, upload.id]);
                                      else setSelectedUploadIds(prev => prev.filter(id => id !== upload.id));
                                    }}
                                  />
                                )}
                                <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
                                  onClick={() => setPreviewUpload(upload)}>
                                  {isImage && (upload.cloudfront_url || upload.s3_url) ? (
                                    <img src={upload.cloudfront_url || upload.s3_url} alt={upload.original_filename} className="w-full h-full object-cover" />
                                  ) : isVideo && upload.thumbnail_url ? (
                                    <img src={upload.thumbnail_url} alt={upload.original_filename} className="w-full h-full object-cover" />
                                  ) : isVideo ? (
                                    <div className="flex flex-col items-center text-gray-400">
                                      <Play className="w-8 h-8" />
                                      <span className="text-[10px] mt-1">Video</span>
                                    </div>
                                  ) : (
                                    <File className="w-8 h-8 text-gray-400" />
                                  )}
                                </div>
                                <div className="p-2">
                                  <p className="text-xs font-medium truncate">{upload.original_filename}</p>
                                  <p className="text-[10px] text-muted-foreground">{upload.uploader_name} · {upload.file_size ? `${(upload.file_size / 1024 / 1024).toFixed(1)} MB` : ''}</p>
                                </div>
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                  <button onClick={(e) => { e.stopPropagation(); setPreviewUpload(upload); }}
                                    className="p-1 bg-white/90 dark:bg-gray-800/90 rounded shadow-sm hover:bg-white"><Eye className="w-3.5 h-3.5" /></button>
                                  {upload.media_file_id ? (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setDownloadCodeUpload({ id: upload.id, media_file_id: upload.media_file_id, original_filename: upload.original_filename }); }}
                                      className="p-1 bg-white/90 dark:bg-gray-800/90 rounded shadow-sm hover:bg-white text-[10px] font-mono font-bold text-purple-600"
                                    >U</button>
                                  ) : (
                                    <button disabled className="p-1 rounded text-[10px] font-mono text-gray-400 cursor-not-allowed">U</button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : uploadViewMode === 'tile' ? (
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                          {(request.uploads || []).map((upload: any) => {
                            const isImage = (upload.mime_type || '').startsWith('image');
                            const isVideo = (upload.mime_type || '').startsWith('video');
                            return (
                              <div key={upload.id} className={`relative border rounded-lg overflow-hidden cursor-pointer group ${
                                selectedUploadIds.includes(upload.id) ? 'ring-2 ring-blue-500' : 'hover:border-blue-300'
                              }`} onClick={() => setPreviewUpload(upload)}>
                                <div className="aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                  {isImage && (upload.cloudfront_url || upload.s3_url) ? (
                                    <img src={upload.cloudfront_url || upload.s3_url} alt={upload.original_filename} className="w-full h-full object-cover" />
                                  ) : isVideo && upload.thumbnail_url ? (
                                    <img src={upload.thumbnail_url} alt={upload.original_filename} className="w-full h-full object-cover" />
                                  ) : isVideo ? (
                                    <Play className="w-6 h-6 text-gray-400" />
                                  ) : (
                                    <File className="w-6 h-6 text-gray-400" />
                                  )}
                                </div>
                                <p className="text-[10px] truncate px-1 py-0.5">{upload.original_filename}</p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* List view (default) */
                        (request.uploads || []).map((upload: any) => {
                          const isImage = (upload.mime_type || '').startsWith('image');
                          const isVideo = (upload.mime_type || '').startsWith('video');
                          return (
                            <div key={upload.id} className={`flex items-center gap-3 p-2.5 border rounded-lg transition-colors ${
                              selectedUploadIds.includes(upload.id) ? 'border-indigo-300 bg-indigo-50/50 dark:bg-indigo-950/20' : 'hover:bg-muted/50'
                            }`}>
                              {(isBuyerHead || isAdmin) && (
                                <input type="checkbox" className="rounded border-border w-4 h-4 shrink-0"
                                  checked={selectedUploadIds.includes(upload.id)}
                                  onChange={e => {
                                    if (e.target.checked) setSelectedUploadIds(prev => [...prev, upload.id]);
                                    else setSelectedUploadIds(prev => prev.filter(id => id !== upload.id));
                                  }}
                                />
                              )}
                              {/* Thumbnail */}
                              <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0 cursor-pointer"
                                onClick={() => setPreviewUpload(upload)}>
                                {isImage && (upload.cloudfront_url || upload.s3_url) ? (
                                  <img src={upload.cloudfront_url || upload.s3_url} alt="" className="w-full h-full object-cover" />
                                ) : isVideo && upload.thumbnail_url ? (
                                  <img src={upload.thumbnail_url} alt="" className="w-full h-full object-cover" />
                                ) : isVideo ? (
                                  <Play className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <File className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{upload.original_filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  By {upload.uploader_name} · {formatDateTime(upload.created_at)}
                                  {upload.file_size ? ` · ${(upload.file_size / 1024 / 1024).toFixed(2)} MB` : ''}
                                  {upload.comments ? ` · ${upload.comments}` : ''}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => setPreviewUpload(upload)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" title="Preview">
                                  <Eye className="w-4 h-4 text-gray-500" />
                                </button>
                                {upload.media_file_id ? (
                                  <button
                                    onClick={() => setDownloadCodeUpload({ id: upload.id, media_file_id: upload.media_file_id, original_filename: upload.original_filename })}
                                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs font-mono font-bold text-purple-600" title="Download with Code (UCODE)"
                                  >U</button>
                                ) : (
                                  <button disabled className="p-1.5 rounded text-xs font-mono text-gray-400 cursor-not-allowed" title="Preparing…">U</button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Preview Modal */}
                      {previewUpload && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center" onClick={() => setPreviewUpload(null)}>
                          <div className="relative max-w-4xl max-h-[90vh] w-full mx-4" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setPreviewUpload(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300">
                              <X className="w-6 h-6" />
                            </button>
                            {(previewUpload.mime_type || '').startsWith('video') ? (
                              <video src={previewUpload.cloudfront_url || previewUpload.s3_url} controls autoPlay
                                poster={previewUpload.thumbnail_url || undefined}
                                className="w-full max-h-[80vh] rounded-lg" />
                            ) : (previewUpload.mime_type || '').startsWith('image') ? (
                              <img src={previewUpload.cloudfront_url || previewUpload.s3_url} alt={previewUpload.original_filename} className="w-full max-h-[80vh] object-contain rounded-lg" />
                            ) : (
                              <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center">
                                <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-lg font-medium">{previewUpload.original_filename}</p>
                                <p className="text-sm text-muted-foreground mt-1">{previewUpload.file_size ? `${(previewUpload.file_size / 1024 / 1024).toFixed(2)} MB` : ''}</p>
                                {previewUpload.media_file_id ? (
                                  <button
                                    onClick={() => { setPreviewUpload(null); setDownloadCodeUpload({ id: previewUpload.id, media_file_id: previewUpload.media_file_id, original_filename: previewUpload.original_filename }); }}
                                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-mono font-bold"
                                  >Download with UCODE</button>
                                ) : (
                                  <button disabled className="mt-4 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">Preparing…</button>
                                )}
                              </div>
                            )}
                            <div className="mt-2 text-white text-center">
                              <p className="text-sm font-medium">{previewUpload.original_filename}</p>
                              <p className="text-xs text-gray-300">{previewUpload.uploader_name} · {formatDateTime(previewUpload.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Reassignment History */}
            {(request.reassignments || []).length > 0 && (
              <div className="py-4">
                <SectionHeader title="Reassignment History" sectionKey="history" />
                {showSection.history && (
                  <div className="mt-2 space-y-2">
                    {(request.reassignments || []).map((r: any, i: number) => (
                      <div key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 bg-muted rounded">{r.reassign_type}</span>
                        <span>{r.from_name} → {r.to_name}</span>
                        {r.reason && <span className="italic">"{r.reason}"</span>}
                        <span className="ml-auto text-xs">{formatDateTime(r.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Upload Progress Queue Sidebar ─────────────────────────────────── */}
      {showUploadQueue && uploadQueue.length > 0 && (
        <div className="fixed bottom-6 right-6 z-[60] w-72 bg-background border border-border rounded-xl shadow-2xl overflow-hidden">
          {/* Queue header */}
          <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
            <div className="flex items-center gap-2">
              {uploading ? (
                <Loader2 className="w-4 h-4 text-primary animate-spin" />
              ) : uploadQueue.some(i => i.status === 'error') ? (
                <AlertCircle className="w-4 h-4 text-destructive" />
              ) : (
                <CheckCircle className="w-4 h-4 text-green-600" />
              )}
              <span className="text-sm font-semibold">
                {uploading
                  ? `Uploading ${uploadQueue.filter(i => i.status === 'done' || i.status === 'uploading').length}/${uploadQueue.length}`
                  : uploadQueue.some(i => i.status === 'error')
                  ? 'Upload complete (with errors)'
                  : 'Upload complete'}
              </span>
            </div>
            <button
              onClick={() => setShowUploadQueue(false)}
              className="p-1 hover:bg-accent rounded transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="h-1 bg-muted">
            <div
              className={`h-1 transition-all duration-300 ${
                uploadQueue.some(i => i.status === 'error') ? 'bg-destructive' : 'bg-primary'
              }`}
              style={{
                width: `${uploadQueue.length > 0
                  ? Math.round((uploadQueue.filter(i => i.status === 'done' || i.status === 'error').length / uploadQueue.length) * 100)
                  : 0}%`
              }}
            />
          </div>
          {/* File list */}
          <div className="max-h-52 overflow-y-auto divide-y divide-border">
            {uploadQueue.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 px-4 py-2.5">
                <span className="shrink-0">
                  {item.status === 'done' && <CheckCircle className="w-4 h-4 text-green-600" />}
                  {item.status === 'uploading' && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
                  {item.status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />}
                  {item.status === 'error' && <AlertCircle className="w-4 h-4 text-destructive" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{item.name}</p>
                  {item.status === 'error' && item.error && (
                    <p className="text-xs text-destructive truncate">{item.error}</p>
                  )}
                  {item.status === 'uploading' && (
                    <p className="text-xs text-primary">Uploading...</p>
                  )}
                  {item.status === 'done' && (
                    <p className="text-xs text-green-600">Done</p>
                  )}
                  {item.status === 'pending' && (
                    <p className="text-xs text-muted-foreground">Waiting...</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* UCODE Download Code Modal */}
      {downloadCodeUpload && (
        <DownloadCodeModal
          mediaFileId={downloadCodeUpload.media_file_id}
          filename={downloadCodeUpload.original_filename}
          onClose={() => setDownloadCodeUpload(null)}
          onDownload={(url, dlFilename) => {
            setDownloadCodeUpload(null);
            const a = document.createElement('a');
            a.href = url;
            a.download = dlFilename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}
        />
      )}
    </div>
  );
}
